import { describeFareGaps } from "./flight-search";
import type { Estimate, HotelRate } from "./types";
import { cityKey } from "./workspace";

/** One line in the chat's live work log: what is happening right now, with real specifics. */
export interface LiveStep {
  id: string;
  label: string;
  /** Result or progress, e.g. "14 flights · cheapest TAP Air Portugal $182". */
  detail: string | null;
  status: "running" | "done" | "failed";
  startedAt: number;
  endedAt: number | null;
}

/**
 * A running step.
 * @param id - Stable id within its tool call.
 * @param label - What is happening.
 * @param detail - Progress so far, if any.
 * @returns The step.
 * @throws Never.
 */
export const startStep = (id: string, label: string, detail: string | null = null): LiveStep => ({
  id,
  label,
  detail,
  status: "running",
  startedAt: Date.now(),
  endedAt: null,
});

/**
 * Updates one step in a list (adding it if new), keeping its start time.
 * @param steps - Current steps.
 * @param id - Step id.
 * @param patch - Fields to change; `status` other than running stamps the end time.
 * @param label - Label used when the step is new.
 * @returns The new list.
 * @throws Never.
 */
export const patchStep = (
  steps: LiveStep[],
  id: string,
  patch: Partial<Pick<LiveStep, "label" | "detail" | "status">>,
  label = ""
): LiveStep[] => {
  const existing = steps.find((step) => step.id === id) ?? startStep(id, label);
  const next: LiveStep = {
    ...existing,
    ...patch,
    endedAt: patch.status && patch.status !== "running" ? (existing.endedAt ?? Date.now()) : existing.endedAt,
  };
  return steps.some((step) => step.id === id) ? steps.map((step) => (step.id === id ? next : step)) : [...steps, next];
};

/**
 * Work-log steps for reading the estimate: one per city's flights ("SAN → SFO", then
 * "Southwest WN 4955 · $129", or why no route is priced) and one per city's hotel rates ("23 venues").
 * @param steps - Current steps, so start times survive updates.
 * @param estimate - The latest estimate.
 * @param hotels - Hotel rates keyed by city key.
 * @param say - Formats a USD amount in the display currency.
 * @returns The updated steps.
 * @throws Never.
 */
export const estimateSteps = (
  steps: LiveStep[],
  estimate: Estimate,
  hotels: ReadonlyMap<string, HotelRate>,
  say: (usd: number) => string
): LiveStep[] => {
  let next = steps;
  for (const city of estimate.cities) {
    const flyers = city.flights.routes.filter((route) => route.status !== "local");
    if (flyers.length > 0) {
      const from = [...new Set(flyers.map((route) => route.airports?.origin ?? route.origin))].join(", ");
      const to = flyers.find((route) => route.airports)?.airports?.destination ?? city.city;
      const priced = flyers.filter((route) => route.usd !== null);
      const cheapest = priced.sort((a, b) => (a.usd ?? 0) - (b.usd ?? 0))[0];
      const flight = cheapest?.flight;
      // An unpriced city says why (fares not published yet, too many searches, no airport),
      // not "no fares found" as if the search had looked and come back empty.
      const gaps = flyers.flatMap((route) => (route.usd === null && route.reason ? [route.reason] : []));
      const detail = city.flights.loading
        ? priced.length
          ? `${priced.length} of ${flyers.length} routes priced`
          : "Searching"
        : cheapest
          ? `${[flight?.airline, flight?.flightNumber].filter(Boolean).join(" ") || "Cheapest"} · ${say(cheapest.usd ?? 0)}`
          : (describeFareGaps(gaps) ?? "No fares found");
      next = patchStep(
        next,
        `flights:${city.city}`,
        { label: `${from} → ${to}`, detail, status: city.flights.loading ? "running" : "done" },
        `${from} → ${to}`
      );
    }
    const rate = hotels.get(cityKey(city.city));
    next = patchStep(
      next,
      `hotel:${city.city}`,
      {
        detail: city.hotel.loading
          ? null
          : rate?.status === "venues"
            ? `${rate.venueCount} venues`
            : rate?.status === "error"
              ? "Couldn't load rates"
              : "No rate data, left out of the total",
        status: city.hotel.loading ? "running" : "done",
      },
      `Hotel rates in ${city.city}`
    );
  }
  return next;
};
